// src/utils/groupCopy.js
//
// COPY A REPEATING GROUP ONTO A CARD (KPR-859; keepr-api utils/groupCopy.js,
// SCHEMA "Copying a repeating group onto a card"). The card editor's "Copy a
// group from…" puts a group another card of the collection defines, or one a
// starter installed here supplied, into the DRAFT as an ordinary new element,
// marked with where it came from. Nothing is sent until Save: the save folds
// the marks into the request-time `copyGroups` instruction, and every other
// send (the change preview, the driven preview) strips them, so a mark is
// never stored on an element.
//
// The pure half: the mark, the fold, the names a copy may not take, the
// facts a dialog row shows, and the answer's notice.

import { apiSlug, effectiveElementNames } from './conversions';
import { formatCount, joinMeta } from './counts';

/** The draft element's mark: the row's `source`, as the API listed it. */
export const COPY_MARK = 'copyOf';

/** The request-time key the save sends beside `elements`. */
export const COPY_KEY = 'copyGroups';

const isObject = v => !!v && typeof v === 'object' && !Array.isArray(v);

/** Is this draft element a copy waiting for the save? */
export const isCopy = element => isObject(element) && isObject(element[COPY_MARK]);

/**
 * The element a copy adds to the draft: the listed group (`row.element`),
 * under `name` when the person gave one (its label then the name they typed,
 * the plural left for the API to derive), marked with `row.source`.
 */
export function copiedElement(row, name = null) {
	const element = { ...(row?.element || {}) };
	const typed = typeof name === 'string' ? name.trim() : '';
	if (typed) {
		element.name = apiSlug(typed);
		element.label = { singular: typed, plural: '' };
	}
	element[COPY_MARK] = row?.source || null;
	return element;
}

/** The elements with every copy mark taken off (a new list only when one was). */
export function withoutCopyMarks(elements) {
	if (!Array.isArray(elements) || !elements.some(isCopy)) return elements;
	return elements.map((element) => {
		if (!isCopy(element)) return element;
		const { [COPY_MARK]: dropped, ...rest } = element; // eslint-disable-line no-unused-vars
		return rest;
	});
}

/**
 * A card payload as the SAVE sends it: the marks off the elements, and
 * `copyGroups` naming each marked element by the name it saves under. A
 * payload with no copy comes back as it was.
 */
export function withGroupCopies(payload) {
	const elements = payload?.elements;
	if (!Array.isArray(elements) || !elements.some(isCopy)) return payload;
	const names = effectiveElementNames(elements);
	const copies = [];
	elements.forEach((element, index) => {
		if (isCopy(element)) copies.push({ element: names[index], source: element[COPY_MARK] });
	});
	return { ...payload, elements: withoutCopyMarks(elements), [COPY_KEY]: copies };
}

/** Any send that is not the save: the marks off, and no instruction. */
export function withoutGroupCopies(payload) {
	const elements = payload?.elements;
	if (!Array.isArray(elements) || !elements.some(isCopy)) return payload;
	return { ...payload, elements: withoutCopyMarks(elements) };
}

/**
 * Every name a copy may not take on this card (the API's 409 `name_taken`):
 * the draft's own elements (under the names they save as), the elements of
 * the cards it extends and of its element sets, and the own elements of every
 * card beneath it.
 */
export function namesInUse({ elements = [], carried = [], descendants = [] } = {}) {
	const names = new Set(effectiveElementNames(elements).filter(Boolean));
	(carried || []).forEach((el) => { if (el?.name) names.add(el.name); });
	(descendants || []).forEach(card => (card?.elements || []).forEach((el) => { if (el?.name) names.add(el.name); }));
	return names;
}

/** The name a copy saves under: the group's own, or the slug of the label typed. */
export const copyNameOf = (row, typed = '') => {
	const text = typeof typed === 'string' ? typed.trim() : '';
	return text ? apiSlug(text) : (row?.element?.name || row?.group?.name || '');
};

/** A row's facts, the platform way: "3 columns · up to 60 entries · at least 1 · headings". */
export function rowFacts(row) {
	return joinMeta(
		formatCount(row?.columns, 'column'),
		row?.maxEntries ? `up to ${row.maxEntries} ${row.maxEntries === 1 ? 'entry' : 'entries'}` : null,
		row?.minEntries ? `at least ${row.minEntries}` : null,
		row?.sections ? 'headings' : null,
	);
}

/** The label a row's group is shown by. */
export const groupLabelOf = row => row?.group?.label?.singular || row?.group?.name || 'Group';

/** A stable key for a row (its source). */
export const rowKey = row => [row?.source?.kind, row?.source?.card, row?.source?.package, row?.source?.group].filter(Boolean).join(':');

/**
 * The save's word on what it could not finish for its copies (`groupCopy` in
 * the answer): one line per column rule the API could not save on the copy,
 * and per starter copy whose tie to the starter was not recorded; [] when
 * everything came with it.
 */
export function rulesNotCopiedLines(groupCopy) {
	const missed = Array.isArray(groupCopy?.rulesNotCopied) ? groupCopy.rulesNotCopied : [];
	const unrecorded = Array.isArray(groupCopy?.recordNotWritten) ? groupCopy.recordNotWritten : [];
	return [
		...missed.map(rule => `${rule.column ? `${rule.element} › ${rule.column}` : rule.element}: ${rule.message || 'the rule could not be copied.'}`),
		...unrecorded.map(name => `${name}: keepr could not record which starter it came from, so the starter's updates will not reach it.`),
	];
}
